import React, { useEffect, useState } from "react";
import { Box, Typography, Radio, Button, Chip, Paper, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import NightShelterIcon from "@mui/icons-material/NightShelter";

const IncidentManagement = ({ updateFormData, nextStep, formData }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  // State management
  const [incidentCategory, setIncidentCategory] = useState("physical");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showIndoorOptions, setShowIndoorOptions] = useState(false);
  const [showFloorOptions, setShowFloorOptions] = useState(false);
  const [showRoomOptions, setShowRoomOptions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedPlace, setSelectedPlace] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [showOutsideOptions, setOutsideOptions] = useState(false);
  const [selectedPath, setSelectedPath] = useState([]);

  useEffect(() => {
    if (formData && Array.isArray(formData) && formData.length > 0) {
      setSelectedPath(formData);

      setSelectedLocation(formData[0] || "");
      setSelectedPlace(formData[1] || "");
      setSelectedFloor(formData[2] || "");

      if (formData[0] === "Inside") {
        setShowIndoorOptions(true);
        if (formData[1] === "Indoor") {
          setShowFloorOptions(true);
          if (formData[2] === "Ground floor") {
            setShowRoomOptions(true);
          }
        }
      } else {
        setOutsideOptions(true);
      }
    }
  }, [formData]);

  const handleCategoryChange = (category) => {
    setIncidentCategory(category);
  };

  const toggleLocationDropdown = () => {
    setShowLocationDropdown(!showLocationDropdown);

    // Always reset location selection to reselect
    setSelectedLocation("");
    setSelectedPlace("");
    setSelectedFloor("");
    setShowIndoorOptions(false);
    setShowFloorOptions(false);
    setShowRoomOptions(false);
    setOutsideOptions(false);
    setSelectedPath([]); // Clear previous selection to allow reselection
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);

    if (location === "Inside") {
      setShowIndoorOptions(true);
      setShowFloorOptions(false);
      setShowRoomOptions(false);
      setOutsideOptions(false);
    } else {
      setOutsideOptions(true);
      setShowIndoorOptions(false);
      setShowFloorOptions(false);
      setShowRoomOptions(false);
    }
  };

  const handleIndoorSelect = (option) => {
    setSelectedPlace(option);
    if (option === "Indoor") {
      setShowFloorOptions(true);
      setShowRoomOptions(false);
    }
  };

  const handleFloorSelect = (floor) => {
    setSelectedFloor(floor);
    if (floor === "Ground floor") {
      setShowRoomOptions(true);
    }
  };

  return (
    <Box
      sx={{
        pl: { xs: 2, sm: 3, md: 4 },
        pr: { xs: 2, sm: 3, md: 4 },
        ml: { xs: 1, sm: 1, md: 2 },
      }}
    >
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Non - Physical Intervention</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />
      <Grid container>
        <Grid item lg={10} md={10}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Incident Category</Typography>

            <Grid container>
              <Grid item xs={12} sm={12} md={6}>
                <Box
                  sx={{
                    border: incidentCategory === "physical" ? "2px solid #1751D0" : "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                    width: { xs: "100%", sm: "45%", md: "45%" },
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                  onClick={() => handleCategoryChange("physical")}
                >
                  <Radio
                    checked={incidentCategory === "physical"}
                    size='small'
                    sx={{
                      p: 0,
                      mr: 1,
                      color: "#999",
                      "&.Mui-checked": {
                        color: "#1751D0",
                      },
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: "14px", color: "black" }}>Physical Intervention</Typography>
                    <Typography sx={{ fontSize: 11 }}>
                      (for example contingent touch, friendly hug or redirection)
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Location Section */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>location</Typography>

            <Grid container>
              <Grid item xs={12} sm={12} md={6}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    onClick={toggleLocationDropdown}
                    sx={{
                      width: { xs: "100%", sm: "45%", md: "45%" },
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: 15,
                      color: selectedLocation ? "inherit" : "#888",
                      "&:hover": {
                        borderColor: "#1751D0",
                        color: "#1751D0",
                      },
                      ...(showLocationDropdown && {
                        borderColor: "#1751D0",
                        color: "blue",
                      }),
                    }}
                  >
                    <Typography sx={{ fontSize: 15 }}>{selectedLocation || "Select the place"}</Typography>
                    <KeyboardArrowRightIcon
                      sx={{
                        transform: showLocationDropdown ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        fontSize: 20,
                        color: "inherit",
                      }}
                    />
                  </Box>
                  {selectedPath.length === 0 ? (
                    <>
                      {/* First Level Dropdown - Outside/Inside */}
                      {showLocationDropdown && (
                        <Paper
                          elevation={2}
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: { xs: "100%", sm: "50%", md: "50%" },
                            mt: 0.5,
                            zIndex: 10,
                            borderRadius: 1,
                          }}
                        >
                          {[
                            {
                              label: "Outside",
                              description: "A quiet suburban street, lined with trees and parked cars.",
                              value: "Outside",
                            },
                            {
                              label: "Inside",
                              description: "A cozy living room with soft lighting, a couch, and a coffee table.",
                              value: "Inside",
                            },
                          ].map(({ label, description, value }) => (
                            <Box
                              key={value}
                              sx={{
                                p: 1.5,
                                borderBottom: "1px solid #e0e0e0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                bgcolor: value === "Inside" && showIndoorOptions ? "#f0f7ff" : "inherit",
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                              onClick={() => handleLocationSelect(value)}
                            >
                              <Box>
                                <Typography sx={{ fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ fontSize: 12, color: "#888", mt: 0.5 }}>{description}</Typography>
                              </Box>
                              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                            </Box>
                          ))}
                        </Paper>
                      )}

                      {/* Second Level - Indoor/Outdoor Options */}
                      {showOutsideOptions && (
                        <Paper
                          elevation={2}
                          sx={{
                            position: "absolute",
                            top: { xs: "calc(100% + 5px)", sm: 120 },
                            left: { xs: 0, sm: "calc(50% + 10px)" },
                            width: { xs: "100%", sm: "40%", md: "45%" },
                            zIndex: 11,
                            borderRadius: 1,
                          }}
                        >
                          {["In Car", "In School", "In Street"].map((option) => (
                            <Box
                              key={option}
                              sx={{
                                p: 1.5,
                                borderBottom: "1px solid #e0e0e0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                              onClick={() => {
                                setSelectedPlace(option);
                                setSelectedPath([selectedLocation, option]); // Use option directly
                              }}
                            >
                              <Typography sx={{ fontSize: 15 }}>{option}</Typography>
                              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                            </Box>
                          ))}
                        </Paper>
                      )}
                      {/* Second Level - Indoor/Outdoor Options */}
                      {showIndoorOptions && (
                        <Paper
                          elevation={2}
                          sx={{
                            position: "absolute",
                            top: { xs: "calc(100% + 5px)", sm: 120 },
                            left: {
                              xs: 0,
                              sm: "calc(50% + 10px)",
                              md: "calc(50% + 10px)",
                            },
                            width: { xs: "100%", sm: "40%", md: "60%" },
                            zIndex: 11,
                            borderRadius: 1,
                          }}
                        >
                          {["Indoor", "Outdoor"].map((option) => (
                            <Box
                              key={option}
                              sx={{
                                p: 1.5,
                                borderBottom: "1px solid #e0e0e0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                              onClick={() => {
                                handleIndoorSelect(option);
                                option === "Outdoor" && setSelectedPath([selectedLocation, option]);
                              }}
                            >
                              <Typography sx={{ fontSize: 15 }}>{option}</Typography>
                              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                            </Box>
                          ))}
                        </Paper>
                      )}

                      {/* Third Level - Floor Options */}
                      {showFloorOptions && (
                        <Paper
                          elevation={2}
                          sx={{
                            position: "absolute",
                            top: { xs: "calc(100% + 10px)", sm: 150 },
                            left: {
                              xs: 0,
                              sm: "calc(90% + 20px)",
                              md: "calc(110% + 20px)",
                            },
                            width: { xs: "100%", sm: "40%", md: "60%" },
                            zIndex: 12,
                            borderRadius: 1,
                          }}
                        >
                          {["Ground floor", "1st floor", "2nd floor", "3rd floor"].map((floor) => (
                            <Box
                              key={floor}
                              sx={{
                                p: 1.5,
                                borderBottom: floor !== "3rd floor" ? "1px solid #e0e0e0" : "none",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                bgcolor: floor === "Ground floor" && showRoomOptions ? "#f0f7ff" : "inherit",
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                              onClick={() => {
                                handleFloorSelect(floor);
                                floor !== "Ground floor" && setSelectedPath([selectedLocation, selectedPlace, floor]);
                              }}
                            >
                              <Typography sx={{ fontSize: 15 }}>{floor}</Typography>
                              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                            </Box>
                          ))}
                        </Paper>
                      )}

                      {/* Fourth Level - Room Options */}
                      {showRoomOptions && (
                        <Paper
                          elevation={2}
                          sx={{
                            position: "absolute",
                            top: { xs: "calc(100% + 15px)", sm: 200, md: 120 },
                            left: {
                              xs: 0,
                              sm: "calc(120% + 60px)",
                              md: "calc(170% + 30px)",
                            },
                            width: { xs: "100%", sm: "40%", md: "60%" },
                            zIndex: 13,
                            borderRadius: 1,
                          }}
                        >
                          {["Bedroom", "Lobby", "Kitchen", "Bedroom 02"].map((room, index, arr) => (
                            <Box
                              key={room}
                              sx={{
                                p: 1.5,
                                borderBottom: index !== arr.length - 1 ? "1px solid #e0e0e0" : "none",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": { bgcolor: "#f5f5f5" },
                              }}
                              onClick={() => {
                                setSelectedPath([selectedLocation, selectedPlace, selectedFloor, room]); // Update the path dynamically
                                setShowRoomOptions(false); // Hide dropdown after selection
                              }}
                            >
                              <Typography sx={{ fontSize: 15 }}>{room}</Typography>
                              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                            </Box>
                          ))}
                        </Paper>
                      )}
                    </>
                  ) : (
                    <Grid container>
                      <Grid item xs={12} sm={8} lg={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                            mt: 2,
                            p: 2,
                            flexWrap: "wrap",
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            width: { xs: "100%", sm: "80%", md: "100%" },
                          }}
                        >
                          {selectedPath.map((step, index) => (
                            <React.Fragment key={index}>
                              {index === selectedPath.length - 1 ? (
                                // Last item appears on a new line
                                <Box sx={{ width: "100%" }}>
                                  <Grid container>
                                    <Grid item>
                                      <NightShelterIcon />
                                    </Grid>
                                    <Grid item>
                                      <Typography
                                        sx={{
                                          fontSize: 16,
                                          color: "black",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {step}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: 14, color: "#757575" }}>
                                  {step} {" > "}
                                </Typography>
                              )}
                            </React.Fragment>
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            updateFormData({ nonIncidentManagementInfo: selectedPath }); // Call the function properly
            nextStep(); // Call the function properly
          }}
          disabled={selectedPath.length === 0}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default IncidentManagement;
